import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { mapTimbreDesignRowToVoiceToneItem } from "../../service/timbreDesign";
import { voicePerformanceVoicesApi } from "../../service/voicePerformance";
import type { VoiceProfileListResp } from "../../service/voicePerformance";
import { resolvePublicVoicePreviewFullUrl } from "../../service/voicePublic";
import { request } from "../../utils/request";

export interface VoiceOption {
  id: number;
  name: string;
  description?: string;
  gender: string;
  genderType: "female" | "male" | "neutral";
  style: string;
  avatarUrl: string;
  previewAudioUrl?: string;
  source: "public" | "mine";
}

export function resolvePreviewUrl(rawUrl?: string): string | undefined {
  if (!rawUrl) return undefined;
  if (rawUrl.startsWith("http://")) return `https://${rawUrl.slice("http://".length)}`;
  if (rawUrl.startsWith("https://")) return rawUrl;
  const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
  return `${VIDEO_CDN_PREFIX}${path}`;
}

export function toVoiceOption(v: VoiceProfileListResp): VoiceOption {
  return {
    id: v.id,
    name: v.displayName,
    description: v.description || "",
    genderType: v.gender === "female" ? "female" : v.gender === "male" ? "male" : "neutral",
    gender: v.gender === "female" ? "女声" : v.gender === "male" ? "男声" : "中性",
    style: v.scene || "通用",
    avatarUrl: v.avatarUrl,
    previewAudioUrl: resolvePreviewUrl(v.previewAudioUrl),
    source: "public",
  };
}

export function toMineVoiceOption(v: VoicePublic.VoiceToneItem): VoiceOption {
  const genderRaw = (v.genderTextValue || "").trim();
  let genderType: VoiceOption["genderType"] = "neutral";
  if (genderRaw.includes("女")) genderType = "female";
  else if (genderRaw.includes("男")) genderType = "male";
  return {
    id: v.id,
    name: v.voiceName || "",
    description: v.description || "",
    genderType,
    gender: genderRaw || "中性",
    style: "",
    avatarUrl: "",
    previewAudioUrl: resolvePublicVoicePreviewFullUrl(v, VIDEO_CDN_PREFIX) || undefined,
    source: "mine",
  };
}

async function fetchMineVoiceOptionsApi(keyword: string): Promise<VoicePublic.VoiceToneItem[]> {
  try {
    const res = await request<TimbreDesign.TimbreDesignPageResponse>(
      "/video/timbre-design/page",
      {
        method: "GET",
        params: {
          pageNo: 1,
          pageSize: 100,
          ...(keyword ? { voiceName: keyword } : {}),
        },
      }
    );
    if (res.code === 0 && res.data && Array.isArray(res.data.list)) {
      return res.data.list.map(mapTimbreDesignRowToVoiceToneItem);
    }
    return [];
  } catch {
    return [];
  }
}

const VOICE_COLORS = [
  "#ec4899", "#334155", "#2563eb", "#f97316",
  "#0f766e", "#7c3aed", "#0891b2", "#dc2626",
  "#ca8a04", "#059669", "#9333ea", "#db2777",
];

export function getVoiceColor(id: number): string {
  return VOICE_COLORS[((id - 1) % VOICE_COLORS.length + VOICE_COLORS.length) % VOICE_COLORS.length];
}

export function useVoiceSelectLists() {
  const [publicVoices, setPublicVoices] = useState<VoiceOption[]>([]);
  const [mineVoices, setMineVoices] = useState<VoiceOption[]>([]);
  const [mineVoicesLoading, setMineVoicesLoading] = useState(false);
  const lastMineKeywordRef = useRef<string | null>(null);

  useEffect(() => {
    const fn = async () => {
      try {
        const res = await voicePerformanceVoicesApi();
        if (res.code === 0 && Array.isArray(res.data)) {
          setPublicVoices(res.data.map(toVoiceOption));
        }
      } catch {
        // ignore
      }
    };
    void fn();
  }, []);

  const reloadMineVoices = useCallback(async (keyword: string) => {
    if (lastMineKeywordRef.current === keyword) return;
    lastMineKeywordRef.current = keyword;
    setMineVoicesLoading(true);
    try {
      const list = await fetchMineVoiceOptionsApi(keyword);
      setMineVoices(list.map(toMineVoiceOption));
    } finally {
      setMineVoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadMineVoices("");
  }, [reloadMineVoices]);

  const allVoices = useMemo(
    () => [...publicVoices, ...mineVoices],
    [publicVoices, mineVoices]
  );

  return {
    publicVoices,
    mineVoices,
    allVoices,
    mineVoicesLoading,
    reloadMineVoices,
  };
}
